import { useSearchParams } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  addMinutes,
  calculateDateDifference,
  clearRideForDuplicate,
  ComputedDataKey,
  getProhibitedToDuplicateFields,
  getResolvedDuplicatedDto,
  IRideDto,
  Log,
  NEW_SUPPLIER_RIDE_LEAD_MINUTES,
  prohibitedToDuplicateFieldsSF,
  RequestBodyModel,
  reverseStationsOrder,
  RIDE_REVERSE_SUFFIX,
  RIDE_REVERSED_PARAM_VALUE,
  RideCrudModeKey,
  RideStatusEnum,
  shiftStationsInfoArrivalTimes,
  useTimezone,
} from "@ryde/shared";

export const useRideDuplicate = () => {
  const [searchParams] = useSearchParams();
  const { timezone } = useTimezone();
  const prohibitedToDuplicateControls = useRecoilValue(
    prohibitedToDuplicateFieldsSF(RideCrudModeKey.uiUnit),
  );

  const processRideDuplicateDto = useEvent(
    ({
      rideDto,
      reverseStations,
      forNewSupplier = false,
    }: {
      rideDto: Partial<IRideDto> | RequestBodyModel;
      reverseStations: boolean;
      // RYDE-7633: when duplicating a past taxi ride to re-dispatch it to a new supplier, strip the
      // old supplier and reschedule the ride to NEW_SUPPLIER_RIDE_LEAD_MINUTES from now (duration kept).
      forNewSupplier?: boolean;
    }): Partial<IRideDto> => {
      const prohibitedToDuplicateFields = getProhibitedToDuplicateFields({
        dto: rideDto,
        controls: prohibitedToDuplicateControls,
      });

      const reverseParam = searchParams.get(RIDE_REVERSE_SUFFIX);
      const shouldReverse =
        reverseParam === RIDE_REVERSED_PARAM_VALUE || reverseStations;

      const cleanedRideDto = clearRideForDuplicate({
        rideDto: rideDto as Partial<IRideDto>,
        reverseStations: shouldReverse,
      });

      const baseDto: Partial<IRideDto> = {
        ...cleanedRideDto,
        status: RideStatusEnum.New,
        isCreatedManually: true,
        [ComputedDataKey]: {
          ...(rideDto?.[ComputedDataKey] || {}),
          isNewTemplate: false,
          isDuplicate: true,
        },
        metadata: {
          ...cleanedRideDto.metadata,
          duplicatedFromRide: shouldReverse
            ? undefined
            : (rideDto as Partial<IRideDto>)?.id,
          returnOfRide: shouldReverse
            ? (rideDto as Partial<IRideDto>)?.id
            : undefined,
        },
      };

      let rideDtoNormalized = getResolvedDuplicatedDto({
        dto: baseDto,
        prohibitedToDuplicateFields,
      }) as Partial<IRideDto>;

      if (shouldReverse) {
        rideDtoNormalized = reverseStationsOrder(
          rideDtoNormalized as IRideDto,
          timezone,
        );
        Log.debug(
          "Ride reverted on duplicate by QS param value",
          rideDtoNormalized,
        );
      }

      if (forNewSupplier) {
        const originalRide = rideDto as Partial<IRideDto>;
        const newRideStart = addMinutes(
          new Date(),
          NEW_SUPPLIER_RIDE_LEAD_MINUTES,
        );
        const durationMinutes =
          originalRide?.rideStart && originalRide?.rideEnd
            ? calculateDateDifference(
                originalRide.rideStart,
                originalRide.rideEnd,
                "minute",
              )
            : 0;

        rideDtoNormalized = {
          ...rideDtoNormalized,
          // Drop the old supplier (clearRideForDuplicate carries it forward) so the new ride can be
          // dispatched to a different supplier. externalMetadata is already omitted by the cleaner.
          supplierId: undefined,
          rideStart: newRideStart,
          rideEnd:
            durationMinutes > 0
              ? addMinutes(newRideStart, durationMinutes)
              : rideDtoNormalized.rideEnd,
          metadata: {
            ...rideDtoNormalized.metadata,
            // Re-base the per-stop times too: shifting only rideStart/rideEnd leaves the nested
            // stationsInfo arrival times in the past, and ride creation does not rebuild them for
            // course rides or custom station routes.
            stationsInfo: shiftStationsInfoArrivalTimes({
              stationsInfo: rideDtoNormalized.metadata?.stationsInfo,
              originalRideStart: originalRide?.rideStart,
              newRideStart,
            }),
          },
        };
      }

      return rideDtoNormalized;
    },
  );

  return { processRideDuplicateDto };
};
